import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecipes } from '@/context/RecipeContext';
import type { RecipeListItem } from '@/types';
import { cn } from '@/lib/utils';

export function RecipesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    recipes,
    categories,
    selectedCategory,
    isLoading,
    error,
    loadRecipes,
    loadCategories,
    selectCategory,
    selectRecipe,
    startInterview,
    activeRecipe,
  } = useRecipes();

  const [showPreview, setShowPreview] = useState(false);
  const [previewRecipe, setPreviewRecipe] = useState<RecipeListItem | null>(null);

  useEffect(() => {
    loadCategories();
    loadRecipes();
  }, [loadCategories, loadRecipes]);

  // Handle ?start=slug parameter to auto-start a recipe
  useEffect(() => {
    const startSlug = searchParams.get('start');
    if (startSlug && !activeRecipe) {
      handleStartRecipe(startSlug);
    }
  }, [searchParams, activeRecipe]);

  const handleStartRecipe = async (slug: string) => {
    await selectRecipe(slug);
  };

  useEffect(() => {
    if (activeRecipe) {
      startInterview(activeRecipe);
      navigate(`/recipes/${activeRecipe.slug}/interview`);
    }
  }, [activeRecipe, startInterview, navigate]);

  const handlePreview = (recipe: RecipeListItem) => {
    setPreviewRecipe(recipe);
    setShowPreview(true);
  };

  const getCategoryIcon = (category: string | null) => {
    switch (category) {
      case 'business':
        return 'storefront';
      case 'portfolio':
        return 'person';
      case 'landing':
        return 'rocket_launch';
      case 'ecommerce':
        return 'shopping_cart';
      case 'blog':
        return 'article';
      default:
        return 'web';
    }
  };

  const getCategoryColor = (category: string | null) => {
    switch (category) {
      case 'business':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'portfolio':
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'landing':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'ecommerce':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'blog':
        return 'bg-pink-500/20 text-pink-400 border-pink-500/30';
      default:
        return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  if (isLoading && recipes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-100">Recipe Book</h1>
        <p className="text-zinc-400 mt-1">
          Choose a template to create your website in minutes
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {/* Category Filter */}
      {categories.length > 0 && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => selectCategory(null)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
              selectedCategory === null
                ? 'bg-brand text-white'
                : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
            )}
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => selectCategory(category)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap capitalize',
                selectedCategory === category
                  ? 'bg-brand text-white'
                  : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
              )}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      {/* Empty State */}
      {recipes.length === 0 && !isLoading && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
            <span className="material-icons text-3xl text-zinc-500">auto_awesome</span>
          </div>
          <h2 className="text-xl font-semibold text-zinc-200 mb-2">No recipes available</h2>
          <p className="text-zinc-400 text-center max-w-md">
            Check back soon for new website templates.
          </p>
        </div>
      )}

      {/* Recipes Grid */}
      {recipes.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recipes.map((recipe) => (
            <div
              key={recipe.id}
              className="bg-elevated rounded-lg border border-zinc-800 overflow-hidden hover:border-zinc-700 transition-colors group"
            >
              {/* Thumbnail */}
              <div className="aspect-video bg-zinc-800 relative">
                {recipe.thumbnail_url ? (
                  <img
                    src={recipe.thumbnail_url}
                    alt={recipe.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="material-icons text-6xl text-zinc-600">
                      {recipe.icon || getCategoryIcon(recipe.category)}
                    </span>
                  </div>
                )}
                {/* Overlay with actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                  <button
                    onClick={() => handlePreview(recipe)}
                    className="px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors flex items-center gap-2"
                  >
                    <span className="material-icons text-sm">visibility</span>
                    Preview
                  </button>
                  <button
                    onClick={() => handleStartRecipe(recipe.slug)}
                    className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors flex items-center gap-2"
                  >
                    <span className="material-icons text-sm">play_arrow</span>
                    Start
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <h3 className="font-semibold text-zinc-100">{recipe.name}</h3>
                  {recipe.category && (
                    <span
                      className={cn(
                        'px-2 py-0.5 text-xs rounded-full border capitalize',
                        getCategoryColor(recipe.category)
                      )}
                    >
                      {recipe.category}
                    </span>
                  )}
                </div>

                {recipe.description && (
                  <p className="text-sm text-zinc-400 line-clamp-2">{recipe.description}</p>
                )}

                <div className="flex items-center justify-between text-xs text-zinc-500 pt-2">
                  <span className="flex items-center gap-1">
                    <span className="material-icons text-sm">group</span>
                    {recipe.use_count} uses
                  </span>
                  {recipe.is_system && (
                    <span className="flex items-center gap-1">
                      <span className="material-icons text-sm text-brand">verified</span>
                      Official
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewRecipe && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="bg-elevated rounded-lg border border-zinc-700 max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <div>
                <h2 className="text-lg font-semibold text-zinc-100">{previewRecipe.name}</h2>
                <p className="text-sm text-zinc-400">{previewRecipe.description}</p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <span className="material-icons text-zinc-400">close</span>
              </button>
            </div>

            {/* Preview Content */}
            <div className="aspect-video bg-zinc-900">
              {previewRecipe.thumbnail_url ? (
                <img
                  src={previewRecipe.thumbnail_url}
                  alt={previewRecipe.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <span className="material-icons text-8xl text-zinc-700 mb-4">
                      {previewRecipe.icon || getCategoryIcon(previewRecipe.category)}
                    </span>
                    <p className="text-zinc-500">Preview not available</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-zinc-800">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-zinc-700 text-zinc-200 rounded-lg hover:bg-zinc-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  handleStartRecipe(previewRecipe.slug);
                }}
                className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors flex items-center gap-2"
              >
                <span className="material-icons text-sm">play_arrow</span>
                Start This Recipe
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
